import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle, XCircle, Lightbulb, Building2, Clock, IndianRupee } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet";
import ExploreMore from "@/components/ExploreMore";
import useCanonical from "@/hooks/useCanonical";
import useMetaDescription from "@/hooks/useMetaDescription";

const COMPARISON_DATA = [
  { feature: "Installation Time", modular: "1-3 days for 50 workstations", traditional: "4-8 weeks for 50 workstations", winner: "modular" },
  { feature: "Customization", modular: "Configured to exact specs at factory, consistent finish every time", traditional: "Handcrafted on-site, finish varies by carpenter skill", winner: "modular" },
  { feature: "Reconfigurability", modular: "Fully modular — expand, rearrange, or relocate entire setups", traditional: "Fixed once built — changes require demolition and rebuild", winner: "modular" },
  { feature: "Quality Control", modular: "Factory-inspected against ISO 9001:2015 quality standards before dispatch", traditional: "Depends on individual carpenter, no standardization", winner: "modular" },
  { feature: "On-Site Mess", modular: "Zero — pre-built components assembled cleanly on-site", traditional: "Significant — sawdust, paint fumes, noise for weeks", winner: "modular" },
  { feature: "Cost (50+ seats)", modular: "INR 6,000-14,000 per workstation (factory-direct pricing)", traditional: "INR 8,000-20,000+ per unit (labour + material + waste)", winner: "modular" },
  { feature: "Warranty", modular: "Manufacturer warranty with after-sales support", traditional: "Limited or no warranty", winner: "modular" },
  { feature: "Tech Integration", modular: "Built-in cable management, power modules, acoustic panels", traditional: "Requires separate electrical and cable work", winner: "modular" },
  { feature: "Scalability", modular: "Order additional units anytime with consistent specifications", traditional: "Replicate exact finish is nearly impossible", winner: "modular" },
  { feature: "Sustainability", modular: "Premium prelaminated particle boards, minimal waste", traditional: "Higher material waste from on-site cutting", winner: "modular" },
];

const RECOMMENDATIONS = [
  { condition: "If your office is under 500 sq ft", recommendation: "Linear workstations with shared cable trays are the most space-efficient layout. Modular partitions with acoustic membranes maintain privacy without consuming floor area. Budget approximately INR 4-5 lakh for a 10-person setup.", icon: Building2 },
  { condition: "If you have 50-200 employees", recommendation: "L-shaped cluster workstations provide 30% more desk space than linear layouts. Combine with height-adjustable desks for key teams and ergonomic task chairs. Budget INR 8,000-12,000 per workstation all-inclusive.", icon: Building2 },
  { condition: "If you are a startup scaling fast", recommendation: "Choose reconfigurable modular systems that grow with you. Modular furniture lets you add workstations in hours rather than rebuilding from scratch. Start with essentials — expand as headcount grows without replacing existing furniture.", icon: TrendingUp },
  { condition: "If you are setting up a new office from scratch", recommendation: "Opt for a turnkey solution covering workstations, chairs, conference tables, storage, and partitions. Ezeefit's 9-step execution process delivers a fully furnished office in 3-6 weeks, including 3D design, manufacturing, and professional installation.", icon: Clock },
  { condition: "If your budget is limited", recommendation: "Factory-direct pricing eliminates middleman markups. Standard linear workstations start at just INR 6,000 per seat. Prioritise quality task chairs (INR 4,000+) as they directly impact day-to-day productivity and long-term durability.", icon: IndianRupee },
  { condition: "If you need a co-working or hybrid workspace", recommendation: "Hot-desking setups with modular pods and phone booths are ideal. Acoustic meeting pods (from INR 1.25 lakh) provide private call spaces. Integrate height-adjustable desks for members who alternate between sitting and standing.", icon: Lightbulb },
];

function TrendingUp(props) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>
  );
}

export default function BuyersGuidePage() {
  useCanonical("https://ezeefitofficesystems.in/buyers-guide");
  useMetaDescription("Modular office furniture vs traditional carpentry — compare install time, cost, quality & scalability for Mumbai corporates. Ezeefit buyer's guide.");

  useEffect(() => {
    document.title = "Modular Furniture vs Traditional Carpentry: A Buyer's Guide for Mumbai Corporates | Ezeefit";
  }, []);

  return (
    <div className="pt-20" data-testid="buyers-guide-page">
      <Helmet>
        <meta name="description" content="Modular office furniture vs traditional carpentry — compare install time, cost, quality & scalability for Mumbai corporates. Ezeefit buyer's guide." />
        <link rel="canonical" href="https://ezeefitofficesystems.in/buyers-guide" />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org", "@type": "FAQPage",
        "mainEntity": [
          {"@type": "Question", "name": "Is modular furniture cheaper than traditional carpentry for offices?", "acceptedAnswer": {"@type": "Answer", "text": "Yes, for offices with 50+ seats. Factory-direct modular workstations cost INR 6,000-14,000 per seat compared to INR 8,000-20,000+ for traditional carpentry. Modular furniture eliminates on-site labour costs, material waste, and the hidden costs of weeks-long disruption to office operations."}},
          {"@type": "Question", "name": "How long does it take to set up a modular office vs traditional carpentry?", "acceptedAnswer": {"@type": "Answer", "text": "Modular office furniture can be installed in 1-3 days for 50 workstations. Traditional on-site carpentry takes 4-8 weeks for the same scale. Modular systems arrive pre-built from the factory and are assembled on-site without sawdust, paint fumes, or noise."}},
          {"@type": "Question", "name": "Can modular furniture be reconfigured when the office layout changes?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. Modular office systems are designed to be expanded, rearranged, or relocated without damage. Traditional carpentry is fixed once built — any changes require demolition and complete rebuilding at additional cost."}},
          {"@type": "Question", "name": "Which office furniture is recommended for a small office under 500 sq ft?", "acceptedAnswer": {"@type": "Answer", "text": "Linear workstations with shared cable trays are space-efficient for offices under 500 sq ft. Pair with modular acoustic partitions to maintain privacy without consuming floor area. Budget approximately INR 4-5 lakh for a 10-person setup."}},
          {"@type": "Question", "name": "Which office furniture suits startups in Mumbai?", "acceptedAnswer": {"@type": "Answer", "text": "Reconfigurable modular systems work well for startups. They scale as your headcount grows — add workstations in hours, not weeks. Start with essential workstations and task chairs, then expand. Factory-direct pricing from manufacturers like Ezeefit helps keep costs low."}}
        ]
      }) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({"@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": [{"@type": "ListItem", "position": 1, "name": "Home", "item": "https://ezeefitofficesystems.in"}, {"@type": "ListItem", "position": 2, "name": "Buyer's Guide", "item": "https://ezeefitofficesystems.in/buyers-guide"}]}) }} />

      {/* Hero */}
      <section className="py-20 bg-[#0F172A]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <Link to="/" className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-white mb-6 transition-colors text-sm">
              <ArrowLeft className="w-4 h-4" /> Back to Home
            </Link>
            <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-3 block">Buyer's Guide</span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              Modular Furniture vs Traditional Carpentry
            </h1>
            <p className="text-lg text-[#94A3B8] max-w-2xl mx-auto" data-testid="hero-description">
              A detailed comparison guide for Mumbai corporates, startups, and growing businesses choosing between modular office furniture systems and traditional on-site carpentry.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Atomic Answer Block */}
      <section className="py-10 bg-[#FFF7ED] border-b border-[#FDBA74]" data-testid="atomic-answer-block">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <p className="text-[#0F172A] font-semibold text-lg leading-relaxed">
            Modular office furniture is 40-60% faster to install, 20-30% more cost-effective at scale, and fully reconfigurable — making it the clear choice for corporates, co-working spaces, and growing businesses over traditional on-site carpentry.
          </p>
        </div>
      </section>

      {/* Detailed Comparison Table */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-8 text-center">
              Head-to-Head Comparison
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full bg-white border border-[#E2E8F0]">
                <thead>
                  <tr className="bg-[#0F172A]">
                    <th className="text-left p-4 text-white font-semibold text-sm">Feature</th>
                    <th className="text-left p-4 text-[#F97316] font-semibold text-sm">Modular Furniture</th>
                    <th className="text-left p-4 text-white/70 font-semibold text-sm">Traditional Carpentry</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_DATA.map((row, i) => (
                    <tr key={row.feature} className={i % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}>
                      <td className="p-4 text-sm font-semibold text-[#0F172A] border-b border-[#E2E8F0]">{row.feature}</td>
                      <td className="p-4 text-sm text-[#334155] border-b border-[#E2E8F0]">
                        <span className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                          {row.modular}
                        </span>
                      </td>
                      <td className="p-4 text-sm text-[#64748B] border-b border-[#E2E8F0]">
                        <span className="flex items-start gap-2">
                          <XCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                          {row.traditional}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Constraint-Based Recommendations */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-3 text-center">
              Which Solution Fits Your Office?
            </h2>
            <p className="text-center text-[#64748B] mb-10">Constraint-based recommendations tailored to your business</p>
            
            <div className="grid md:grid-cols-2 gap-6">
              {RECOMMENDATIONS.map((rec, i) => (
                <motion.div
                  key={rec.condition}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="border border-[#E2E8F0] p-6 hover:border-[#F97316] transition-colors"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <rec.icon className="w-5 h-5 text-[#F97316] mt-0.5 shrink-0" />
                    <h3 className="font-bold text-[#0F172A]">{rec.condition}</h3>
                  </div>
                  <p className="text-sm text-[#64748B] leading-relaxed">{rec.recommendation}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0F172A]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to Make the Switch?
          </h2>
          <p className="text-[#94A3B8] mb-8">
            Get a free consultation and 3D layout for your office. Factory-direct pricing, no middlemen.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild className="bg-[#F97316] hover:bg-orange-600 text-white rounded-none px-8 py-5 font-semibold uppercase tracking-wider text-sm">
              <Link to="/contact">
                Get Free Consultation
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <ExploreMore currentPage="buyers-guide" />
    </div>
  );
}
